import React, { useEffect, useState } from 'react';
import { Layout, Button, notification, Skeleton, Tooltip } from 'antd';
import { SyncOutlined, SearchOutlined, StarOutlined, ClockCircleOutlined, AppstoreOutlined, LoadingOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import SearchBar from './StatusSidebar/SearchBar';
import FavoritesList from './StatusSidebar/FavoritesList';
import RecentList from './StatusSidebar/RecentList';
import PipelineTree from './StatusSidebar/PipelineTree';
import { useSettingsState } from '../../state/settingsState';
import styles from './StatusSidebar/StatusSidebar.module.css';

const { Sider } = Layout;

const StatusSidebar = () => {
  const {
    pipelines,
    statuses,
    selectedStatus,
    searchTerm,
    favorites,
    history,
    setSearchTerm,
    toggleFavoriteStatus,
    recordStatusHistory,
    setSelectedStatus,
    loadFavoritesFromStorage,
    loadHistoryFromStorage,
    updateData
  } = useSettingsState();

  const [loading, setLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [width, setWidth] = useState(300);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [sectionHeights, setSectionHeights] = useState({
    pipelines: 400,
    favorites: 140,
    recent: 140
  });

  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerHeight < 600);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const initializeData = async () => {
      try {
        loadFavoritesFromStorage();
        loadHistoryFromStorage();
        await updateData();
      } finally {
        setIsInitialLoading(false);
      }
    };
    initializeData();
  }, []);

  useEffect(() => {
    if (selectedStatus) {
      recordStatusHistory(selectedStatus.amo_status_id);
    }
  }, [selectedStatus]);

  const syncData = async () => {
    setLoading(true);
    try {
      await updateData(true);
      notification.success({
        message: 'Успех',
        description: 'Статусы успешно синхронизированы',
        placement: 'bottomRight'
      });
    } catch (error) {
      notification.error({
        message: 'Ошибка синхронизации',
        description: 'Не удалось синхронизировать статусы',
        placement: 'bottomRight'
      });
    } finally {
      setLoading(false);
    }
  };

  const favoritesCount = favorites.length;
  const recentCount = history.length;
  const pipelinesCount = Object.keys(pipelines).length;

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const handleSectionResize = (section, newHeight) => {
    setSectionHeights(prev => ({
      ...prev,
      [section]: Math.max(100, Math.min(600, newHeight))
    }));
  };

  if (isInitialLoading) {
    return (
      <Sider width={width} collapsible collapsed={collapsed} className={styles.sidebar}>
        <div className={styles.headerSection}>
          <Skeleton.Button active size="large" style={{ width: '100%', height: 40 }} />
        </div>
        <div className={styles.contentArea}>
          <div className={styles.loadingState}>
            <LoadingOutlined className={styles.loadingIcon} />
            <div>Загрузка данных...</div>
          </div>
        </div>
      </Sider>
    );
  }

  return (
    <Sider 
      width={width} 
      collapsible 
      collapsed={collapsed}
      onCollapse={setCollapsed}
      trigger={null}
      className={styles.sidebar}
      style={{
        overflow: 'hidden',
        transition: 'all 0.2s'
      }}
    >
      <div className={styles.headerSection}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: collapsed ? 0 : 12 }}>
          <Tooltip title={collapsed ? "Развернуть панель" : "Свернуть панель"}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={toggleCollapse}
              style={{
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />
          </Tooltip>
          {!collapsed && (
            <span style={{ fontSize: '14px', fontWeight: 600, color: '#262626' }}>
              Панель навигации
            </span>
          )}
        </div>
        
        {!collapsed && (
          <Button 
            type="primary"
            icon={<SyncOutlined />} 
            onClick={syncData}
            loading={loading}
            className={styles.syncButton}
          >
            Синхронизировать статусы
          </Button>
        )}
        
        {collapsed && (
          <Tooltip title="Синхронизировать статусы" placement="right">
            <Button 
              type="primary"
              icon={<SyncOutlined />} 
              onClick={syncData}
              loading={loading}
              style={{
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />
          </Tooltip>
        )}
      </div>
      
      {!collapsed && (
        <div className={styles.contentArea}>
          <div className={styles.sectionCard}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitle}>
                <SearchOutlined className={styles.sectionIcon} />
                Поиск
              </div>
            </div>
            <SearchBar value={searchTerm} onChange={setSearchTerm} />
          </div>

          <div 
            className={styles.treeSectionCard}
            style={{
              height: sectionHeights.pipelines,
              minHeight: 200,
              maxHeight: 600
            }}
          >
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitle}>
                <AppstoreOutlined className={styles.sectionIcon} />
                Воронки
                {isSmallScreen && (favoritesCount > 0 || recentCount > 0) && (
                  <span style={{ fontSize: '11px', color: '#8c8c8c', marginLeft: '4px' }}>
                    ({favoritesCount > 0 ? `★${favoritesCount}` : ''} {recentCount > 0 ? `⌚${recentCount}` : ''})
                  </span>
                )}
              </div>
              <span className={styles.sectionCount}>{pipelinesCount}</span>
            </div>
            <div className={styles.treeContainer}>
              <PipelineTree
                pipelines={pipelines}
                statuses={statuses}
                searchTerm={searchTerm}
                favorites={favorites}
                history={history}
                selectedStatus={selectedStatus}
                onStatusSelect={setSelectedStatus}
                toggleFavorite={toggleFavoriteStatus}
                expandedKeys={expandedKeys}
                onExpandedKeysChange={setExpandedKeys}
              />
            </div>
          </div>
          <div 
            className={styles.resizeHandle}
            onMouseDown={(e) => {
              const startY = e.clientY;
              const startHeight = sectionHeights.pipelines;
              
              const handleMouseMove = (e) => {
                const newHeight = startHeight + (e.clientY - startY);
                handleSectionResize('pipelines', newHeight);
              };
              
              const handleMouseUp = () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
              };
              
              document.addEventListener('mousemove', handleMouseMove);
              document.addEventListener('mouseup', handleMouseUp);
            }}
          />

          {!isSmallScreen && favoritesCount > 0 && (
            <>
              <div 
                className={styles.sectionCard} 
                style={{ 
                  flexShrink: 0,
                  height: sectionHeights.favorites,
                  minHeight: 100,
                  maxHeight: 300
                }}
              >
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionTitle}>
                    <StarOutlined className={styles.sectionIcon} />
                    Избранное
                  </div>
                  <span className={styles.sectionCount}>{favoritesCount}</span>
                </div>
                <div className={styles.fixedHeightSection} style={{ height: sectionHeights.favorites - 60 }}>
                  <FavoritesList
                    favorites={favorites}
                    pipelines={pipelines}
                    statuses={statuses}
                    onStatusSelect={setSelectedStatus}
                    onToggleFavorite={toggleFavoriteStatus}
                  />
                </div>
              </div>
              <div 
                className={styles.resizeHandle}
                onMouseDown={(e) => {
                  const startY = e.clientY;
                  const startHeight = sectionHeights.favorites;
                  
                  const handleMouseMove = (e) => {
                    const newHeight = startHeight + (e.clientY - startY);
                    handleSectionResize('favorites', newHeight);
                  };
                  
                  const handleMouseUp = () => {
                    document.removeEventListener('mousemove', handleMouseMove);
                    document.removeEventListener('mouseup', handleMouseUp);
                  };
                  
                  document.addEventListener('mousemove', handleMouseMove);
                  document.addEventListener('mouseup', handleMouseUp);
                }}
              />
            </>
          )}

          {!isSmallScreen && recentCount > 0 && (
            <>
              <div 
                className={styles.sectionCard} 
                style={{ 
                  flexShrink: 0,
                  height: sectionHeights.recent,
                  minHeight: 100,
                  maxHeight: 300
                }}
              >
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionTitle}>
                    <ClockCircleOutlined className={styles.sectionIcon} />
                    Недавние
                  </div>
                  <span className={styles.sectionCount}>{recentCount}</span>
                </div>
                <div className={styles.fixedHeightSection} style={{ height: sectionHeights.recent - 60 }}>
                  <RecentList
                    history={history}
                    pipelines={pipelines}
                    statuses={statuses}
                    onStatusSelect={setSelectedStatus}
                  />
                </div>
              </div>
              <div 
                className={styles.resizeHandle}
                onMouseDown={(e) => {
                  const startY = e.clientY;
                  const startHeight = sectionHeights.recent;
                  
                  const handleMouseMove = (e) => {
                    const newHeight = startHeight + (e.clientY - startY);
                    handleSectionResize('recent', newHeight);
                  };
                  
                  const handleMouseUp = () => {
                    document.removeEventListener('mousemove', handleMouseMove);
                    document.removeEventListener('mouseup', handleMouseUp);
                  };
                  
                  document.addEventListener('mousemove', handleMouseMove);
                  document.addEventListener('mouseup', handleMouseUp);
                }}
              />
            </>
          )}
        </div>
      )}
    </Sider>
  );
};

export default StatusSidebar; 